import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Activity, Battery, DollarSign, Trophy, Zap, ChevronRight, Award, Clock } from 'lucide-react';
import {
  fetchProfile, fetchSessions, fetchBadges,
  formatCents, buildAvatarUrl,
} from '@/lib/api';
import { useActiveSessionRows } from '@/hooks/useActiveSession';
import { useAuthStore } from '@/store/auth';
import NamedIcon from '@/components/ui/NamedIcon';
import styles from './DashboardPage.module.css';

interface Props { onNavigate: (path: string) => void }

export function DashboardPage({ onNavigate }: Props) {
  const { t } = useTranslation('dashboard');
  const user = useAuthStore((s) => s.user);
  const profileQ = useQuery({ queryKey: ['profile'], queryFn: fetchProfile });
  // Shared live-session cache (ACTIVE_SESSION_KEY) — the same rows the
  // home, ChargingPage and the mini bar read, so this card can never
  // show a session they have already seen end.
  const activeQ = useActiveSessionRows(10_000);
  const recentQ = useQuery({
    queryKey: ['recent-sessions'],
    queryFn: () => fetchSessions({ limit: 5 }),
  });
  const badgesQ = useQuery({
    queryKey: ['badges'],
    queryFn: fetchBadges,
  });

  const profile = profileQ.data;
  // Only a drawing session belongs on the "charging now" card — a
  // pending shell (plug-in window) has nothing to show yet.
  const active = (activeQ.data ?? []).filter((s) => s.status === 'active');
  const recent = recentQ.data?.data ?? [];
  const earnedBadges = (badgesQ.data ?? []).filter((b) => b.earned).slice(0, 3);

  const rankProgress = profile && profile.next_rank
    ? Math.max(0, Math.min(100,
        ((profile.points - 0) / Math.max(1, profile.points + (profile.points_to_next_rank ?? 0))) * 100
      ))
    : 100;

  return (
    <div className={styles.page}>
      {/* Hero card */}
      {profile && (
        <div className={styles.hero}>
          <img
            className={styles.heroAvatar}
            src={profile.avatar_url ?? buildAvatarUrl(profile.avatar_style ?? 'avataaars-neutral', profile.avatar_seed ?? 'default')}
            alt=""
          />
          <div className={styles.heroInfo}>
            {/*
             * Editorial: no greeting label here. The username IS the
             * hero — adding "Welcome back" / "Hi" / "Driver" above it
             * is throat-clearing that pushes the actual identity line
             * down. Considered alternatives: "Hi" (still throat-
             * clearing, just shorter), "Driver" (redundant with the
             * fallback already in heroName), and a time-of-day
             * greeting (cute but adds runtime branching for zero
             * informational gain). Dropping the label entirely is the
             * call. Don't reintroduce — if a label feels missing,
             * the rank pill below already provides identity context.
             */}
            <h1 className={styles.heroName}>
              {profile.username ?? user?.name?.split(' ')[0] ?? user?.name ?? t('heroFallbackName')}
            </h1>
            {profile.current_rank && (
              <span className={styles.rankPill}>
                <Trophy size={12} /> {profile.current_rank}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Active session */}
      {active.length > 0 && active[0] && (
        <div className={styles.activeCard} onClick={() => onNavigate(`/sessions/${active[0]!._id}`)}>
          <div className={styles.activeHeader}>
            <div className={styles.activePulse}>
              <span className={styles.dot} /> {t('active.chargingNow')}
            </div>
            <ChevronRight size={18} className={styles.chevron} />
          </div>
          <div className={styles.activeStats}>
            <div>
              <p className={styles.activeLabel}>{t('active.energy')}</p>
              <p className={styles.activeValue}>{active[0].energy_kwh.toFixed(2)} <span>kWh</span></p>
            </div>
            <div>
              <p className={styles.activeLabel}>{t('active.costSoFar')}</p>
              <p className={styles.activeValue}>{formatCents(active[0].cost?.total ?? 0, active[0].cost?.currency)}</p>
            </div>
            <div>
              <p className={styles.activeLabel}>{t('active.duration')}</p>
              <p className={styles.activeValue}>{Math.round(active[0].duration_minutes)}<span>m</span></p>
            </div>
          </div>
        </div>
      )}

      {/* Stats grid */}
      <div className={styles.statsGrid}>
        <StatCard icon={Activity} label={t('stat.sessions')} value={profile?.total_sessions ?? 0} color="#3498db" />
        <StatCard icon={Battery} label={t('stat.energy')} value={`${(profile?.total_kwh ?? 0).toFixed(0)} kWh`} color="#9b59b6" />
        <StatCard icon={DollarSign} label={t('stat.points')} value={profile?.points ?? 0} color="#2ecc71" />
        <StatCard icon={Trophy} label={t('stat.position')} value={profile?.leaderboard_position ? `#${profile.leaderboard_position}` : '—'} color="#f39c12" />
      </div>

      {/* Rank progress */}
      {profile?.next_rank && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('nextRank.title', { rank: profile.next_rank })}</h2>
            <span className={styles.sectionMeta}>{t('nextRank.toGo', { count: profile.points_to_next_rank })}</span>
          </div>
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{ width: `${rankProgress}%` }} />
          </div>
        </div>
      )}

      {/* Recent badges */}
      {earnedBadges.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}><Award size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} /> {t('badges.title')}</h2>
            <button className={styles.linkBtn} onClick={() => onNavigate('/profile')}>{t('badges.viewAll')} <ChevronRight size={14} /></button>
          </div>
          <div className={styles.badgeRow}>
            {earnedBadges.map((b) => (
              <div key={b._id} className={styles.badgeChip}>
                <span className={`${styles.rarity} ${styles[`r_${b.rarity}`]}`} />
                <span className={styles.badgeName}>{b.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent sessions */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>{t('sessions.title')}</h2>
          <button className={styles.linkBtn} onClick={() => onNavigate('/sessions')}>{t('sessions.seeAll')} <ChevronRight size={14} /></button>
        </div>
        {recentQ.isLoading ? (
          <p className={styles.empty}>{t('sessions.loading')}</p>
        ) : recent.length === 0 ? (
          <p className={styles.empty}>{t('sessions.empty')}</p>
        ) : (
          <div className={styles.sessionList}>
            {recent.map((s) => (
              <button key={s._id} className={styles.sessionItem} onClick={() => onNavigate(`/sessions/${s._id}`)}>
                <div className={styles.sessionIcon}>
                  <NamedIcon name="bolt" fallback={<Zap size={16} />} size={16} />
                </div>
                <div className={styles.sessionInfo}>
                  <p className={styles.sessionDate}>{new Date(s.started_at).toLocaleDateString()}</p>
                  <p className={styles.sessionMeta}>
                    <Clock size={11} style={{ verticalAlign: 'middle' }} /> {Math.round(s.duration_minutes)}m · {s.energy_kwh.toFixed(1)} kWh
                  </p>
                </div>
                <div className={styles.sessionAmount}>
                  <span className={styles.amount}>{formatCents(s.cost?.total ?? 0, s.cost?.currency)}</span>
                  <ChevronRight size={16} className={styles.chevron} />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Find chargers shortcut */}
      <button className={styles.findCta} onClick={() => onNavigate('/map')}>
        <NamedIcon name="bolt" fallback={<Zap size={18} />} size={18} />
        <span>{t('findCharger')}</span>
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }: { icon: React.ComponentType<{ size: number }>; label: string; value: string | number; color: string }) {
  return (
    <div className={styles.statCard}>
      <div className={styles.statIcon} style={{ background: `${color}15`, color }}>
        <Icon size={18} />
      </div>
      <p className={styles.statLabel}>{label}</p>
      <p className={styles.statValue}>{value}</p>
    </div>
  );
}
